import React, { useRef } from 'react';
import { chartElementAtEvent } from '../chartInteraction';
import type { Chart as ChartJS } from 'chart.js';
import { Chart } from 'react-chartjs-2';
import { type TooltipItem } from 'chart.js';
import { type Context as DataLabelContext } from 'chartjs-plugin-datalabels';
import KpiChartPanel from './KpiChartPanel';
import { formatAxisCompactK } from '../chartFormatters';
import { KPI_BAR_SEQUENCE } from './kpiChartPalette';

export type BarValueFormat = 'count' | 'percent';

export interface KpiChangeLabeledBarChartProps {
  title: string;
  labels: string[];
  values: number[];
  datasetLabel: string;
  valueFormat: BarValueFormat;
  emptyMessage?: string;
  /** Index of clicked bar — parent maps to drill dimension. */
  onBarClick?: (barIndex: number) => void;
}

const KpiChangeLabeledBarChart: React.FC<KpiChangeLabeledBarChartProps> = ({
  title,
  labels,
  values,
  datasetLabel,
  valueFormat,
  emptyMessage = 'No data for the selected filters.',
  onBarClick,
}) => {
  const chartRef = useRef<ChartJS<'bar'>>(null);
  const maxV = Math.max(0, ...values, valueFormat === 'percent' ? 2 : 1);
  const suggestedMax =
    valueFormat === 'percent' ? Math.min(100, Math.ceil(maxV * 1.25 * 10) / 10) : maxV * 1.12;

  if (!labels.length || !values.length) {
    return (
      <KpiChartPanel title={title} className="h-full" flexFill titleClassName="text-center">
        <p className="px-4 py-10 text-center text-sm text-slate-500">{emptyMessage}</p>
      </KpiChartPanel>
    );
  }

  const handleClick = (event: React.MouseEvent<HTMLCanvasElement>) => {
    if (!onBarClick || !chartRef.current) return;
    const els = chartElementAtEvent(chartRef.current, event);
    const el = els[0];
    if (el == null) return;
    const idx = el.index;
    if (values[idx] == null || values[idx] <= 0) return;
    onBarClick(idx);
  };

  return (
    <KpiChartPanel title={title} className="h-full" flexFill titleClassName="text-center">
      <div className="relative min-h-[260px] w-full flex-1">
        <Chart
          ref={chartRef}
          type="bar"
          onClick={onBarClick ? handleClick : undefined}
          data={{
            labels,
            datasets: [
              {
                label: datasetLabel,
                data: values,
                backgroundColor: labels.map((_, i) => KPI_BAR_SEQUENCE[i % KPI_BAR_SEQUENCE.length]),
                borderRadius: 6,
                barThickness: valueFormat === 'percent' ? 36 : 32,
                datalabels: {
                  display: true,
                  anchor: 'end',
                  align: 'end',
                  offset: 4,
                  color: '#111827',
                  font: { weight: 600, size: 10 },
                  formatter: (value: number, ctx: DataLabelContext) => {
                    const v = Number(ctx.dataset.data[ctx.dataIndex]);
                    if (valueFormat === 'percent') {
                      return `${Number.isFinite(v) ? v.toFixed(1) : '0'}%`;
                    }
                    return String(v);
                  },
                },
              },
            ],
          }}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            layout: { padding: { top: 22, right: 8, left: 4, bottom: 4 } },
            onHover: (event, elements) => {
              const canvas = event.native?.target;
              if (!(canvas instanceof HTMLElement)) return;
              const el = elements[0];
              const idx = el?.index;
              const v = idx !== undefined ? values[idx] : undefined;
              canvas.style.cursor = onBarClick && v != null && v > 0 ? 'pointer' : 'default';
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                callbacks: {
                  label: (ctx: TooltipItem<'bar'>) => {
                    const raw = Number(ctx.raw);
                    if (valueFormat === 'percent') {
                      return `${ctx.dataset.label}: ${raw.toFixed(2)}%`;
                    }
                    return `${ctx.dataset.label}: ${raw}`;
                  },
                },
              },
            },
            scales: {
              x: {
                grid: { display: false },
                ticks: { color: '#6b7280', font: { size: 10 }, maxRotation: 40 },
              },
              y: {
                beginAtZero: true,
                suggestedMax,
                border: { display: false },
                grid: { color: 'rgba(0,0,0,0.06)' },
                ticks: {
                  color: '#9ca3af',
                  maxTicksLimit: 8,
                  callback: (v) =>
                    valueFormat === 'percent' ? `${v}%` : formatAxisCompactK(v as number),
                },
              },
            },
          }}
        />
      </div>
    </KpiChartPanel>
  );
};

export default KpiChangeLabeledBarChart;
